import { Accessor, createEffect, createSignal, onCleanup } from "solid-js";

import { Channel } from "stoat.js";

import { useClient } from "@revolt/client";
import { useVoice } from "@revolt/rtc";

export type DmCallActionLabel = "Start Call" | "Join Call" | "Leave Call";
export type CallActionLabel = DmCallActionLabel | "Connecting...";

export type DmCallStateResponse = {
  handled: boolean;
  state: string;
  participants: string[];
  ui_hints?: {
    action_label?: string;
  };
};

type Listener = (state: DmCallStateResponse | undefined) => void;
type Fetcher = () => Promise<DmCallStateResponse | undefined>;
type Entry = {
  fetcher: Fetcher;
  listeners: Set<Listener>;
  value?: DmCallStateResponse;
  timer?: number;
  inflight?: Promise<void>;
};

const POLL_MS = 2000;
const channels = new Map<string, Entry>();

function notify(entry: Entry) {
  for (const listener of entry.listeners) {
    listener(entry.value);
  }
}

async function refreshChannelState(channelId: string) {
  const entry = channels.get(channelId);
  if (!entry) return;

  if (entry.inflight) {
    await entry.inflight;
    return;
  }

  entry.inflight = (async () => {
    try {
      entry.value = await entry.fetcher();
    } catch {
      entry.value = undefined;
    }

    notify(entry);
  })().finally(() => {
    if (channels.get(channelId) === entry) {
      entry.inflight = undefined;
    }
  });

  await entry.inflight;
}

function ensureChannelEntry(channelId: string, fetcher: Fetcher) {
  let entry = channels.get(channelId);

  if (!entry) {
    entry = {
      fetcher,
      listeners: new Set<Listener>(),
    };
    channels.set(channelId, entry);
  } else {
    entry.fetcher = fetcher;
  }

  if (typeof window !== "undefined" && !entry.timer) {
    entry.timer = window.setInterval(() => {
      void refreshChannelState(channelId);
    }, POLL_MS);
    void refreshChannelState(channelId);
  }

  return entry;
}

function subscribeChannelState(
  channelId: string,
  fetcher: Fetcher,
  listener: Listener,
) {
  const entry = ensureChannelEntry(channelId, fetcher);
  entry.listeners.add(listener);
  listener(entry.value);

  return () => {
    const current = channels.get(channelId);
    if (!current) return;

    current.listeners.delete(listener);

    if (current.listeners.size === 0) {
      if (current.timer) {
        window.clearInterval(current.timer);
      }
      channels.delete(channelId);
    }
  };
}

export function normalizeDmCallActionLabel(
  action: string | undefined,
): DmCallActionLabel | undefined {
  if (action === "Start Call" || action === "Join Call" || action === "Leave Call") {
    return action;
  }
  return undefined;
}

export function resolveCallActionLabel(options: {
  isConnecting: boolean;
  isConnected: boolean;
  hasExistingCallToJoin: boolean;
  serviceAction?: string;
}): CallActionLabel {
  if (options.isConnecting) return "Connecting...";
  if (options.isConnected) return "Leave Call";

  const serviceAction = normalizeDmCallActionLabel(options.serviceAction);
  if (serviceAction) return serviceAction;

  if (options.hasExistingCallToJoin) return "Join Call";
  return "Start Call";
}

export function useSharedDmCallState(
  channel: Channel,
  refreshDeps?: Accessor<unknown>,
) {
  const client = useClient();
  const voice = useVoice();
  const [dmCallState, setDmCallState] = createSignal<DmCallStateResponse>();
  const isDmChannel = () => channel.type === "DirectMessage";

  const fetchState = async () => {
    if (!isDmChannel()) {
      return undefined;
    }

    try {
      return (await client().api.get(
        `/channels/${channel.id}/call_state`,
      )) as DmCallStateResponse;
    } catch {
      return undefined;
    }
  };

  const refresh = () => {
    if (!isDmChannel()) return;
    void refreshChannelState(channel.id);
  };

  createEffect(() => {
    if (!isDmChannel()) {
      setDmCallState(undefined);
      return;
    }

    const unsubscribe = subscribeChannelState(channel.id, fetchState, setDmCallState);
    onCleanup(unsubscribe);
  });

  createEffect(() => {
    if (!isDmChannel()) return;

    void voice.state();
    void voice.channel()?.id;
    if (refreshDeps) void refreshDeps();

    refresh();
  });

  return {
    dmCallState,
    refresh,
    isDmChannel,
  };
}
